/* eslint-disable react/display-name */
import React, {
    useState,
    useEffect,
    useRef,
    forwardRef,
    useImperativeHandle,
} from 'react'
import SelectRC, { Option } from 'rc-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faSpinner } from '@fortawesome/pro-duotone-svg-icons'
import { compareItem } from '@/Components/DisplayItems/Utils/FilterSelect'

interface FastSwitchOption {
    id: string
    value: string
    label: React.ReactNode
}

interface UserFieldFastSwitchProps {
    selected: { id: string }[]
    options: FastSwitchOption[]
    isSingleSelect: boolean
    isInvalid: boolean
    userLoading: boolean

    itemId: string
    placeholder: string
    errors?: string[] | string
    userDisplay: (label: React.ReactNode) => string
    onSearch: (value: string) => void
    onClickLoad: (cb: (options: FastSwitchOption[]) => void) => void
    onFastSwitchChange: (items: any | any[]) => void
}

interface UserFieldEditRef {
    focus: () => void
    blur: () => void
}

export const UserFieldFastSwitch = forwardRef<
    UserFieldEditRef,
    UserFieldFastSwitchProps
>(
    (
        {
            selected,
            options,
            isSingleSelect,
            isInvalid,
            userLoading,
            itemId,
            placeholder,
            errors,
            userDisplay,
            onSearch,
            onClickLoad,
            onFastSwitchChange,
        },
        ref
    ) => {
        const innerRef = useRef<any>(null)

        useImperativeHandle(ref, () => ({
            focus: () => innerRef.current?.focus?.(),
            blur: () => innerRef.current?.blur?.(),
        }))

        // Convert selected prop to FastSwitchOption[]
        const [current, setCurrent] = useState<FastSwitchOption[]>([])

        useEffect(() => {
            if (!selected) {
                setCurrent([])
                return
            }

            const selectedArray = Array.isArray(selected)
                ? selected
                : [selected]

            setCurrent(
                selectedArray.map((item) => ({
                    id: item.id,
                    value: item.id,
                    label: userDisplay(item),
                }))
            )
        }, [selected, userDisplay])

        const filteredOptions = options.filter(
            (opt) => !current.some((sel) => sel.value === opt.value)
        )

        const filterOptions = (input: string, option: any) => {
            const labelText = compareItem(option.props.children)
            return labelText.toLowerCase().includes(input.toLowerCase())
        }

        const handleChange = (selectedItems: any | any[]) => {
            let newSelected: FastSwitchOption[] = []

            if (Array.isArray(selectedItems)) {
                // Multiple select
                newSelected = selectedItems
                    .map(
                        (si) =>
                            options.find((o) => o.value === si.value) ||
                            current.find((o) => o.value === si.value)
                    )
                    .filter(Boolean) as FastSwitchOption[]
            } else if (selectedItems) {
                // Single select
                const si = selectedItems as {
                    value: string
                    label: React.ReactNode
                }
                const found =
                    options.find((o) => o.value === si.value) ||
                    current.find((o) => o.value === si.value)
                if (found) newSelected = [found]
            }

            setCurrent(newSelected)

            onFastSwitchChange(
                isSingleSelect ? newSelected[0] || null : newSelected
            )
        }

        const commonProps = {
            className: `w-100 custom-select-react ${
                isInvalid ? 'border-red' : ''
            }`,
            id: `${itemId}-search`,
            allowClear: true,
            showSearch: true,
            loading: userLoading,
            onSearch,
            placeholder,
            filterOption: filterOptions,
            suffixIcon: (
                <FontAwesomeIcon
                    spin={userLoading}
                    icon={userLoading ? faSpinner : faChevronDown}
                />
            ),
            notFoundContent: 'No items found',
            labelInValue: true,
        }

        return (
            <>
                <SelectRC
                    {...commonProps}
                    ref={innerRef}
                    mode={!isSingleSelect ? 'multiple' : undefined}
                    value={current.map((it) => ({
                        value: it.value,
                        label: userDisplay(it),
                    }))}
                    onChange={handleChange}
                    onClick={() =>
                        onClickLoad((opts) => {
                            // handled by parent
                        })
                    }
                >
                    {filteredOptions.map((opt) => (
                        <Option key={opt.id} value={opt.value}>
                            {opt.label}
                        </Option>
                    ))}
                </SelectRC>

                {isInvalid && (
                    <span className="fake-feedback">
                        {Array.isArray(errors) ? errors[0] : errors}
                    </span>
                )}
            </>
        )
    }
)
